import { useAppDispatch } from "@quest/hooks/redux";
import { useSchool } from "../hooks/use-school";
import {
  setEnableAssignmentScoring,
  setMentorsCanPublishAssignments,
  setSchoolId,
  setUsesIslandAssignmentStructure,
} from "@quest/slice/school/school-slice";
import {
  setPrimary,
  setSecondary,
  setTextColor,
} from "@quest/slice/theme/themeSlice";
import Loader from "@quest/ui/components/loader";
import { useEffect } from "react";
import Cookies from "js-cookie";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { AlertCircle, RefreshCw } from "lucide-react";

function ThemeProvider({ children }: { children: React.ReactNode }) {
  const {
    data: schoolDetails,
    isLoading: isSchoolDetailsLoading,
    isFetching: isSchoolDetailsFetching,
    error,
  } = useSchool();

  const { t: tValidations } = useTranslation("Validations");

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (schoolDetails) {
      dispatch(setSchoolId(schoolDetails?.data?.[0]?.id || ""));
      dispatch(
        setUsesIslandAssignmentStructure(
          schoolDetails?.data?.[0]?.uses_island_assignment_structure || false,
        ),
      );
      dispatch(
        setEnableAssignmentScoring(
          schoolDetails?.data?.[0]?.enable_assignment_scoring || false,
        ),
      );
      dispatch(
        setMentorsCanPublishAssignments(
          schoolDetails?.data?.[0]?.mentors_can_publish_assignments === true,
        ),
      );
    }
  }, [schoolDetails, dispatch]);

  useEffect(() => {
    if (schoolDetails) {
      //setting the theme colors for the app
      document.documentElement.style.setProperty(
        "--primary",
        schoolDetails?.data?.[0]?.primary_color || "#FFD1A5",
      );

      document.documentElement.style.setProperty(
        "--secondary",
        schoolDetails?.data?.[0]?.secondary_color || "#F49B4B",
      );

      document.documentElement.style.setProperty(
        "--text-color",
        schoolDetails?.data?.[0]?.text_color || "black",
      );

      Cookies.set(
        "primary",
        schoolDetails?.data?.[0]?.primary_color || "#FFD1A5",
      );
      Cookies.set(
        "secondary",
        schoolDetails?.data?.[0]?.secondary_color || "#F49B4B",
      );
      Cookies.set(
        "text_color",
        schoolDetails?.data?.[0]?.text_color || "black",
      );
      dispatch(
        setPrimary(schoolDetails?.data?.[0]?.primary_color || "#FFD1A5"),
      );
      dispatch(
        setSecondary(schoolDetails?.data?.[0]?.secondary_color || "#F49B4B"),
      );
      dispatch(setTextColor(schoolDetails?.data?.[0]?.text_color || "black"));
    }
  }, [schoolDetails, dispatch]);

  // Auth errors are handled by AuthGuard / axios interceptor — don't block the tree
  const isAuthError =
    error instanceof AxiosError &&
    (error.response?.status === 401 || error.response?.status === 403);

  if (error instanceof AxiosError && !isAuthError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md rounded-xl border border-red-200 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>

          <h1 className="text-xl font-semibold text-gray-900">
            {tValidations(error?.response?.data?.message) ??
              "An unexpected error occurred. Please try again later."}
          </h1>

          <p className="mt-3 text-sm text-gray-600">
            {tValidations("somethingWentWrong")}
          </p>

          <button
            onClick={() => window.location.reload()}
            className="mt-6 inline-flex items-center gap-2 rounded-lg bg-red-600 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-red-700"
          >
            <RefreshCw className="h-4 w-4" />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if ((isSchoolDetailsLoading || isSchoolDetailsFetching) && !schoolDetails) {
    return <Loader />;
  }

  return <>{children}</>;
}

export default ThemeProvider;
